/* eslint-disable no-param-reassign */
// actions
import * as R from 'ramda'
import {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  RefObject,
  DragEvent,
} from 'react'
import { useSelector } from 'react-redux'
import { usePanelHistoryTop } from 'weblite-router'
import { RoomTypes } from '@weblite/ts-action/room'
import { useConditionalHook } from '@weblite/use-conditional-hook'
import { dispatchDropZoneSetIsOpen } from './components/body/components/fileDropZone/fileDropZone.action'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { editorAPI } from '$comp/_app/components/chat/components/footer/components/send-field/components/text-editor'
import {
  changeFileTypeToValidFileOrAudioMessageType,
  checkIfFileIsAMedia,
} from '$comp/_app/components/chat/components/footer/components/send-field/helper'
import { sendFieldHeightView } from '$comp/main/root.reducer'
import {
  canDeleteMessage,
  canEditMessage,
  getPermissionOnWappMessage,
  canForwardMessage,
  sendNewMessage,
  getOpenedRoomMessageByIidFromReduxOrIndexedDb,
} from '$logic/message'
import { useOpenedRoomPermissions } from '$logic/opened-room'
import {
  useClientUserHasPermissionToForwardMessage,
  useClientUserPermissions,
} from '$logic/subscription/react-hook'
import { isCallStarted, useCallStorage } from '$logic/call'
import { isHall, useRoomAttribute } from '$logic/room'
import { useAsyncFunction } from '$func/async-function.react-hook'
import { nothing } from '$helper/functions/utils'
import { vibrate } from '$func/vibrate'
import { validateMessageFileSizeAndShowError } from '$logic/message/helper'

type EventType = DragEvent<HTMLDivElement>

const preventDefaults = (e: EventType) => {
  e.preventDefault()
  e.stopPropagation()
}

export function useFileDropHandlers({
  roomId,
  dropZoneRef,
  roomType,
}: {
  roomId: string
  dropZoneRef: RefObject<HTMLDivElement>
  roomType: RoomTypes
}) {
  const { openedRoomPermissions } = useOpenedRoomPermissions()

  const onDragLeave = useCallback(
    (e: EventType) => {
      preventDefaults(e)
      const dropZoneEl = dropZoneRef.current
      if (!dropZoneEl) return

      dropZoneEl.style.display = 'none'
      dispatchDropZoneSetIsOpen(false)
      editorAPI.focus()
    },
    [dropZoneRef],
  )

  const onDragOver = useCallback(
    (e: EventType) => {
      preventDefaults(e)
      const dropZoneEl = dropZoneRef.current
      if (!dropZoneEl || !openedRoomPermissions?.sendMedia) return

      dropZoneEl.style.display = 'block'
      dispatchDropZoneSetIsOpen(true)
    },
    [openedRoomPermissions, dropZoneRef],
  )

  const onDrop = useCallback(
    (e: EventType) => {
      preventDefaults(e)
      const dropZoneEl = dropZoneRef.current
      if (!dropZoneEl) return

      dropZoneEl.style.display = 'none'
      dispatchDropZoneSetIsOpen(false)

      const files = R.path(['dataTransfer', 'files'], e) as FileList
      if (!files || !files.length) return

      if (files.length > 1) {
        Array.from(files).forEach(file => {
          if (!validateMessageFileSizeAndShowError(file)) return
          const isMedia = checkIfFileIsAMedia(file)
          if (validateMessageFileSizeAndShowError(file))
            sendNewMessage(
              isMedia
                ? file
                : changeFileTypeToValidFileOrAudioMessageType(file),
              undefined,
              undefined,
              roomId,
              roomType,
            )
        })
      } else {
        if (!validateMessageFileSizeAndShowError(files[0])) return
        const isMedia = checkIfFileIsAMedia(files[0])
        if (validateMessageFileSizeAndShowError(files[0]))
          sendFieldApis.setFile(
            isMedia
              ? files[0]
              : changeFileTypeToValidFileOrAudioMessageType(files[0]),
          )
      }
    },
    [roomId, dropZoneRef],
  )

  return { onDragOver, onDragLeave, onDrop }
}

export const roomSelectionAPI: {
  setSelection: (iid: number) => void
  deleteSelection: (iid: number) => void
} = {
  setSelection: nothing,
  deleteSelection: nothing,
}

export function useMultiSelect() {
  const [selectionMap, setSelectionMap] = useState<Record<number, boolean>>({})

  const selectedItems = useMemo(
    () => R.keys(selectionMap).map(Number),
    [selectionMap],
  )

  const deleteSelection = (iid: number) => setSelectionMap(R.dissoc(iid))

  const setSelection = (iid: number) => {
    setSelectionMap(map => ({ ...map, [iid]: true }))
    vibrate()
  }

  useEffect(() => {
    roomSelectionAPI.setSelection = setSelection
    roomSelectionAPI.deleteSelection = deleteSelection
  }, [])

  const resetSelection = () => setSelectionMap({})

  const hasSelection = selectedItems?.length > 0

  return {
    selectionMap,
    setSelection,
    deleteSelection,
    resetSelection,
    selectedItems,
    hasSelection,
  }
}

type ActionsPermissions = {
  canCopy: boolean
  canEdit: boolean
  canDelete: boolean
  canDeleteWapps: boolean
  canForward: boolean
  protectContent: boolean
}

interface ReturnType extends ActionsPermissions {
  arePermissionsStale?: boolean
}
export function useActionsPermissions(
  roomId: string | undefined,
  selectedIids: number[],
): ReturnType {
  const prevValueRef = useRef<ActionsPermissions>()
  const { value: clientUserHasPermissionToForward } =
    useClientUserHasPermissionToForwardMessage(roomId)

  const { value } = useAsyncFunction(async () => {
    if (!selectedIids?.length)
      return {
        canCopy: false,
        canEdit: false,
        canDelete: false,
        canDeleteWapps: false,
        canForward: false,
        protectContent: false,
      }

    const messages = await Promise.all(
      selectedIids.map(iid =>
        getOpenedRoomMessageByIidFromReduxOrIndexedDb(iid),
      ),
    )

    const deletePermissions = await Promise.all(messages.map(canDeleteMessage))

    const deleteWappsPermissions = await Promise.all(
      messages.map(async message => {
        const { canDeleteWapp } = await getPermissionOnWappMessage(message)
        return canDeleteWapp
      }),
    )
    const editPermissions = await Promise.all(messages.map(canEditMessage))
    const forwardPermissions = messages.map(canForwardMessage)

    const anyHasBody = R.any(
      message => 'body' in message && !!message.body,
      messages,
    )

    return {
      canCopy: anyHasBody,
      canEdit: R.all(Boolean, editPermissions),
      canDelete: R.all(Boolean, deletePermissions),
      canDeleteWapps: R.all(Boolean, deleteWappsPermissions),
      canForward: R.all(Boolean, forwardPermissions),
      protectContent: !clientUserHasPermissionToForward,
    }
  }, [selectedIids, clientUserHasPermissionToForward])

  if (value) prevValueRef.current = value

  useLayoutEffect(() => {
    prevValueRef.current = undefined
  }, [roomId])

  const staleValue = prevValueRef.current
    ? { ...prevValueRef.current, stale: true }
    : null

  return (
    value ||
    staleValue || {
      canCopy: false,
      canEdit: false,
      canDelete: false,
      canDeleteWapps: false,
      canForward: false,
    }
  )
}

export function useFooterHeight() {
  return useSelector(sendFieldHeightView)
}

export function useRoomPinsIsOpen() {
  const topLocation = usePanelHistoryTop('room')?.location

  return useMemo(() => {
    const [lastLocationPiece] = topLocation?.split('/').slice(-1) || []
    return lastLocationPiece === 'pins'
  }, [topLocation])
}

export function useShouldShowCallBars(roomId: string) {
  const roomCall = useRoomAttribute('call', roomId)
  const roomCallStarted = isCallStarted(roomCall)

  const {
    roomId: currentCallRoomId,
    pvMeta: { roomId: currentPvCallRoomId, state: pvState },
  } = useCallStorage()

  const showScheduledCallBar = Boolean(
    roomCall && roomCall.startedAt && roomCall.type && !roomCallStarted,
  )

  const showJoinHallCallBar =
    roomCall &&
    roomCallStarted &&
    !!roomCall.type &&
    roomCall.type !== 'call' &&
    currentCallRoomId !== roomId

  const showCallBar = Boolean(
    !showJoinHallCallBar &&
      (currentCallRoomId || currentPvCallRoomId) &&
      pvState !== 'ended',
  )

  return {
    showNotParticipatedHallCallBar: showJoinHallCallBar || showScheduledCallBar,
    showCallBar,
  }
}

export function useIsClientUserHallAdmin(roomId: string) {
  const roomType = useRoomAttribute('type', roomId)
  const { isAdmin } =
    useConditionalHook(isHall(roomType), {
      hook: useClientUserPermissions,
      hookArgs: roomId,
    }) || {}

  return Boolean(isAdmin)
}
